import { useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { MOBILE_MAPS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import { useMobileMaps } from '@mobileHooks'
import { Add as AddIcon } from '@mui/icons-material'
import { Box, Button } from '@mui/material'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileMaps = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllMaps = matchPath(ROUTES.CONFIG_MOBILE_MAPS, pathname)

  const { maps, loading, copyMap, deleteMap } = useMobileMaps()

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [mapToCopy, setMapToCopy] = useState<{ id: string; name: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [mapToDelete, setMapToDelete] = useState<{ id: string; name: string } | null>(null)

  const handleCreate = () => {
    navigate(ROUTES.CONFIG_MOBILE_MAPS_CREATE)
  }

  const handleEdit = (id: string) => {
    navigate(`${ROUTES.CONFIG_MOBILE_MAPS_EDIT}/${id}`)
  }

  const handleCopyClick = (id: string) => {
    const map = maps.find(m => m.id === id)
    if (map) {
      setMapToCopy({ id: map.id, name: map.name })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = (newName: string) => {
    if (mapToCopy) {
      const newMap = copyMap(mapToCopy.id, newName)
      if (newMap) {
        setCopyDialogOpen(false)
        setMapToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_MAPS_EDIT}/${newMap.id}`)
      }
    }
  }

  const handleDeleteClick = (id: string) => {
    const map = maps.find(m => m.id === id)
    if (map) {
      setMapToDelete({ id: map.id, name: map.name })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = () => {
    if (mapToDelete) {
      deleteMap(mapToDelete.id)
      setDeleteDialogOpen(false)
      setMapToDelete(null)
    }
  }

  const handleExport = (id: string) => {
    const map = maps.find(m => m.id === id)
    if (map) {
      exportToJSON(map, `${map.name}_map_config.json`)
    }
  }

  const columns = [
    ...MOBILE_MAPS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: params => handleEdit(params.row.id),
      onCopy: params => handleCopyClick(params.row.id),
      onDelete: params => handleDeleteClick(params.row.id),
      onDowloadButtonClick: params => handleExport(params.row.id),
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile Map'
        initialName={mapToCopy?.name || ''}
        existingNames={maps.map(m => m.name)}
        onClose={() => {
          setCopyDialogOpen(false)
          setMapToCopy(null)
        }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile Map'
        message='Are you sure you want to delete map config'
        itemName={mapToDelete?.name}
        onClose={() => {
          setDeleteDialogOpen(false)
          setMapToDelete(null)
        }}
        onConfirm={handleDelete}
      />

      <ConfigListPageShell
        title='Mobile Maps'
        isVisible={!!isAllMaps}
        actions={
          <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        }
      >
          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid
              rows={maps}
              columns={columns}
              loading={loading}
              hideFooter
              disableRowSelectionOnClick
            />
          </Box>
      </ConfigListPageShell>
    </>
  )
}
